<script lang="ts" module>
	declare global {
		interface Window {
			grecaptcha: {
				execute(): Promise<void>;
				reset(): void;
				getResponse(): string;
				ready(cb: () => void): void;
			};
		}
	}

	let recaptchaInserted = false;
</script>

<script lang="ts">
	import { initFoxentry } from "@utils/initFoxentry.js";
	import { type Snippet, onMount } from "svelte";
	import type { HTMLFormAttributes } from "svelte/elements";
	import type { MaybePromise } from "@core/utils/MaybePromise.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { handleInvalidInputs } from "./handleInvalidInputs.js";

	let {
		form = $bindable(),
		/* eslint-disable prefer-const */
		children,
		onsubmit,
		enableRecaptcha = false,
		...rest
		/* eslint-enable prefer-const */
	}: Omit<HTMLFormAttributes, "onsubmit"> & {
		form?: HTMLFormElement;
		children: Snippet;
		onsubmit?(this: void, event: SubmitEvent, form: HTMLFormElement): MaybePromise<void>;
		enableRecaptcha?: boolean;
	} = $props();

	const { RECAPTCHA_SITE_KEY, FOXENTRY_KEY } = getGlobalSettings();
	const useRecaptcha = $derived(RECAPTCHA_SITE_KEY !== "" && enableRecaptcha);

	let ready = false;
	async function waitForReadyRecatpcha(): Promise<void> {
		if (ready) {
			return;
		}
		await new Promise<void>((resolve) => {
			const interval = setInterval(() => {
				if (ready) {
					clearInterval(interval);
					resolve();
				}
			}, 500);
		});
	}

	async function handleAntispamSubmit(
		event: SubmitEvent & { currentTarget: EventTarget & HTMLFormElement },
	): Promise<void> {
		const { currentTarget } = event;
		event.preventDefault();

		if (!useRecaptcha) {
			await onsubmit?.(event, currentTarget);
			return;
		}

		await waitForReadyRecatpcha();
		await window.grecaptcha.execute();
		const token = window.grecaptcha.getResponse();
		if (token) {
			window.grecaptcha.reset();
			await onsubmit?.(event, currentTarget);
		}
	}

	function recaptcha(_element: HTMLElement): void {
		async function setup(): Promise<void> {
			await new Promise<void>((resolve) => {
				const interval = setInterval(() => {
					// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-unnecessary-condition
					if (window.grecaptcha) {
						clearInterval(interval);
						resolve();
					}
				}, 250);
			});
			window.grecaptcha.reset();
			window.grecaptcha.ready(() => {
				ready = true;
			});
		}

		if (recaptchaInserted) {
			void setup();
			return;
		}
		const events = ["mousemove", "click", "pointermove", "pointerdown"] as const;
		function listen(): void {
			for (const event of events) {
				window.removeEventListener(event, listen);
			}
			if (recaptchaInserted) {
				void setup();
				return;
			}
			recaptchaInserted = true;
			const script = document.createElement("script");
			script.src = "https://www.google.com/recaptcha/api.js";
			script.async = true;
			document.head.appendChild(script);
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			script.addEventListener("load", setup);
		}
		for (const event of events) {
			window.addEventListener(event, listen);
		}
	}

	onMount(() => {
		initFoxentry(FOXENTRY_KEY);
	});
</script>

<form bind:this={form} use:handleInvalidInputs {...rest} onsubmit={handleAntispamSubmit}>
	{#if useRecaptcha}
		<div class="g-recaptcha z-[10000]" data-sitekey={RECAPTCHA_SITE_KEY} data-size="invisible" use:recaptcha></div>
	{/if}

	{@render children()}
</form>
